import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stamp, Plus, Trash2, Pencil, ChevronDown, ChevronUp, Eye, EyeOff, ShieldCheck,
  ExternalLink, Search, FileUp, FileText, Bell, MessageSquare, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useStepUp } from "../StepUpGuard";
import { ShareDocButton } from "../ShareDoc";

/* Visas & Entry Documents — per-passenger visa records with requirement check,
   application workflow, validity, arrival card, health docs, files, comments & reminders. */

const REQ_OPTS = [["", "Visa required?"], ["required", "Yes — visa required"], ["not_required", "No — not required"],
  ["visa_on_arrival", "Visa on Arrival"], ["evisa", "eVisa"], ["eta", "Electronic Travel Authorization"],
  ["transit_visa", "Transit visa"], ["unknown", "Unknown / Check required"]];
const TYPE_OPTS = [["", "Visa type"], ["tourist", "Tourist / Visitor"], ["business", "Business"], ["schengen", "Schengen"],
  ["transit", "Transit"], ["student", "Student"], ["work", "Work"], ["medical", "Medical"],
  ["family_visit", "Family visit"], ["conference", "Conference"], ["digital_nomad", "Digital nomad"], ["other", "Other…"]];
const FMT_OPTS = [["", "Visa / authorization format"], ["evisa", "Electronic visa / eVisa"], ["email_pdf", "Email / PDF"],
  ["passport_sticker", "Passport sticker"], ["passport_stamp", "Passport stamp"],
  ["linked_to_passport", "Electronic, linked to passport"], ["physical_document", "Physical document / card"],
  ["visa_on_arrival", "Visa on arrival"], ["other", "Other…"]];
const STATUS_OPTS = [["not_started", "Not started"], ["not_required", "Not required"], ["preparing_documents", "Preparing documents"],
  ["ready_to_apply", "Ready to apply"], ["applied", "Applied"], ["processing", "Processing"],
  ["additional_info_requested", "Additional info requested"], ["approved", "Approved"], ["received", "Received"],
  ["rejected", "Rejected"], ["expired", "Expired"], ["cancelled", "Cancelled"]];
const PERIOD_OPTS = [["", "Visa validity"], ["1m", "1 month"], ["2m", "2 months"], ["3m", "3 months"], ["6m", "6 months"],
  ["1y", "1 year"], ["2y", "2 years"], ["5y", "5 years"], ["10y", "10 years"], ["other", "Other…"]];
const ENTRY_OPTS = [["", "Entries allowed"], ["single", "Single entry"], ["double", "Double entry"], ["multiple", "Multiple entry"], ["na", "Not applicable"]];
const HEALTH_TYPES = [["health_card", "Health card"], ["vaccination_certificate", "Vaccination certificate"],
  ["yellow_fever", "Yellow fever certificate"], ["travel_insurance", "Travel insurance"], ["covid_record", "COVID / vaccination record"],
  ["medical_certificate", "Medical certificate"], ["prescription_letter", "Prescription / medication letter"], ["other", "Other…"]];
const FILE_TYPES = [["passport_copy", "Passport copy"], ["visa_application", "Visa application"], ["visa_approval", "Visa approval"],
  ["evisa", "eVisa"], ["passport_visa_page", "Passport visa page"], ["arrival_card", "Arrival card"], ["arrival_qr", "Arrival card QR"],
  ["health_card", "Health card"], ["vaccination_certificate", "Vaccination certificate"], ["travel_insurance", "Travel insurance"],
  ["invitation_letter", "Invitation letter"], ["hotel_confirmation", "Hotel confirmation"], ["flight_confirmation", "Flight confirmation"],
  ["financial_documents", "Financial documents"], ["passport_photos", "Passport photos"], ["other", "Other supporting document"]];
const REMINDER_KINDS = [["apply", "Apply for visa"], ["appointment", "Visa appointment"], ["biometrics", "Biometrics appointment"],
  ["check_status", "Check application status"], ["visa_expiry", "Visa expiration"], ["passport_expiry", "Passport expiration"],
  ["arrival_card", "Arrival card submission"], ["health_cert", "Health certificate"], ["insurance_expiry", "Travel insurance expiration"]];
const BUCKET = {
  complete: { dot: "bg-emerald-500", label: "Complete", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  pending: { dot: "bg-amber-400", label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  action: { dot: "bg-red-500", label: "Action required", cls: "bg-red-50 text-red-700 border-red-300" },
  not_required: { dot: "bg-zinc-300", label: "Not required", cls: "bg-zinc-50 text-zinc-600 border-zinc-300" },
};
const inp = "border border-zinc-300 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";
const statusLabel = (s) => (STATUS_OPTS.find(([v]) => v === s) || ["", s])[1];

export default function VisaEntry({ trip }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // record object or {} for new
  const load = () => api.get(`/trip-visas/trip/${trip.id}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const canEdit = data.my_role === "owner" || data.my_role === "edit";
  const ov = BUCKET[data.summary.overall] || null;

  return (
    <section className="mb-10" data-testid="trip-visas">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-violet-700 text-2xl flex items-center gap-2"><Stamp className="w-6 h-6" /> Visas &amp; Entry Documents</h2>
        {canEdit && (
          <button onClick={() => setEditing({})} data-testid="visa-add-passenger"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add passenger</button>
        )}
      </div>

      {/* Summary card */}
      <div className="border border-violet-200 bg-violet-50/40 p-4 mb-4" data-testid="visa-summary">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span><span className="text-muted-foreground">Travel dates: </span><b>{[data.summary.start_date, data.summary.end_date].filter(Boolean).join(" – ") || "—"}</b></span>
          <span><span className="text-muted-foreground">Passengers: </span><b data-testid="visa-passenger-count">{data.summary.passengers}</b></span>
          {ov && <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs font-700 ${ov.cls}`} data-testid="visa-overall"><span className={`w-2 h-2 rounded-full ${ov.dot}`} /> {ov.label}</span>}
          {data.summary.arrival_cards_missing > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-700 text-amber-700" data-testid="visa-arrival-warning">
              <AlertTriangle className="w-3.5 h-3.5" /> {data.summary.arrival_cards_missing} arrival card{data.summary.arrival_cards_missing > 1 ? "s" : ""} not submitted</span>
          )}
        </div>
        {data.summary.destinations.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {data.summary.destinations.map((c) => (
              <div key={c.iso2} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`visa-dest-${c.iso2}`}>
                <b>{c.name}</b>
                {c.links?.visa_info && <a href={c.links.visa_info} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ShieldCheck className="w-3 h-3" /> Official visa site <ExternalLink className="w-3 h-3" /></a>}
                {c.links?.evisa && <a href={c.links.evisa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">eVisa portal <ExternalLink className="w-3 h-3" /></a>}
                {(c.links?.arrival_card || c.links?.immigration) && <a href={c.links.arrival_card || c.links.immigration} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Arrival / immigration <ExternalLink className="w-3 h-3" /></a>}
                {c.last_verified && <span className="text-muted-foreground">verified {c.last_verified}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
          <Link to="/visas" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="visa-directory-link"><Search className="w-3.5 h-3.5" /> Browse official visa &amp; entry links for all countries</Link>
          <span className="inline-flex items-center gap-1 text-muted-foreground"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Only official government links are shown — beware unofficial visa-processing sites.</span>
        </div>
      </div>

      {editing && <RecordForm trip={trip} record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {data.records.length === 0 && !editing ? (
        <p className="text-sm text-muted-foreground" data-testid="visa-empty">No passenger visa records yet. Add each traveller to track their visa requirement, application, arrival card and documents.</p>
      ) : (
        <div className="space-y-3">
          {data.records.map((r) => <RecordCard key={r.id} rec={r} trip={trip} canEdit={canEdit} onEdit={() => setEditing(r)} onChanged={load} />)}
        </div>
      )}
    </section>
  );
}

function Dot({ bucket }) {
  const b = BUCKET[bucket] || BUCKET.action;
  return <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs font-700 ${b.cls}`}><span className={`w-2 h-2 rounded-full ${b.dot}`} /> {b.label}</span>;
}

function RecordCard({ rec, trip, canEdit, onEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [passport, setPassport] = useState(null);
  const ac = rec.arrival_card || {};
  const reveal = async () => {
    if (passport) { setPassport(null); return; }
    const r = await api.get(`/trip-visas/${rec.id}/reveal`);
    setPassport(r.data.passport_number || "—");
  };
  const remove = async () => { await api.delete(`/trip-visas/${rec.id}`); toast.success("Removed"); onChanged(); };
  return (
    <div className="border border-zinc-300 bg-white" data-testid={`visa-record-${rec.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-3 p-4 text-left" data-testid={`visa-toggle-${rec.id}`}>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-900">{rec.passenger_name}{rec.destination_iso2 ? ` — ${rec.destination_iso2}` : ""}</div>
          <div className="text-xs text-muted-foreground">{statusLabel(rec.status || "not_started")}{rec.validity?.valid_until ? ` · valid until ${rec.validity.valid_until}` : ""}</div>
        </div>
        <Dot bucket={rec.bucket} />
        {ac.required === "yes" && !ac.submitted && <span className="inline-flex items-center gap-1 text-xs font-700 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> Arrival card not submitted</span>}
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && (
        <div className="border-t border-zinc-200 p-4 space-y-4 text-sm">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            <Field l="Passport nationality" v={rec.passport_nationality} />
            <div><span className="text-muted-foreground">Passport no: </span>
              {rec.has_passport_number ? (<><span className="font-mono">{passport || rec.passport_number}</span>
                <button onClick={reveal} className="ml-1.5 text-primary align-middle" data-testid={`visa-reveal-${rec.id}`}>{passport ? <EyeOff className="w-3.5 h-3.5 inline" /> : <Eye className="w-3.5 h-3.5 inline" />}</button></>) : "—"}
            </div>
            <Field l="Passport expiry" v={rec.passport_expiry} />
            <Field l="Requirement" v={(REQ_OPTS.find(([v]) => v === rec.requirement) || [])[1]} />
            <Field l="Visa type" v={rec.visa_type === "other" ? rec.visa_type_other : (TYPE_OPTS.find(([v]) => v === rec.visa_type) || [])[1]} />
            <Field l="Format" v={rec.visa_format === "other" ? rec.visa_format_other : (FMT_OPTS.find(([v]) => v === rec.visa_format) || [])[1]} />
            <Field l="Arrival date" v={rec.arrival_date} />
            <Field l="Departure date" v={rec.departure_date} />
            <Field l="Application ref" v={rec.application?.reference} />
            <Field l="Valid from" v={rec.validity?.valid_from} />
            <Field l="Valid until" v={rec.validity?.valid_until} />
            <Field l="Entries / max stay" v={[(ENTRY_OPTS.find(([v]) => v === rec.validity?.entries) || [])[1], rec.validity?.max_stay_days && `${rec.validity.max_stay_days} days`].filter(Boolean).join(" · ")} />
          </div>
          {rec.checked_at && <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Requirements last checked {rec.checked_at} — recheck before travel; rules change often.</div>}
          {(rec.health_docs || []).length > 0 && (
            <div data-testid={`visa-health-${rec.id}`}>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Health &amp; travel documents</div>
              {rec.health_docs.map((h) => (
                <div key={h.id} className="text-xs">• {(HEALTH_TYPES.find(([v]) => v === h.doc_type) || [])[1] || h.doc_type_other}{h.number ? ` — ${h.number}` : ""}{h.expiry_date ? ` (expires ${h.expiry_date})` : ""}</div>
              ))}
            </div>
          )}
          <VisaFiles rec={rec} canEdit={canEdit} onChanged={onChanged} />
          {rec.notes && <div className="bg-zinc-50 border border-zinc-200 p-2.5 text-xs whitespace-pre-wrap" data-testid={`visa-notes-${rec.id}`}>{rec.notes}</div>}
          <Comments rec={rec} canEdit={canEdit} onChanged={onChanged} />
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-1">
              <button onClick={onEdit} data-testid={`visa-edit-${rec.id}`} className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs hover:border-primary"><Pencil className="w-3.5 h-3.5" /> Edit record</button>
              <ReminderMenu rec={rec} trip={trip} />
              <button onClick={remove} data-testid={`visa-delete-${rec.id}`} className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs hover:border-destructive text-destructive"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Field = ({ l, v }) => <div><span className="text-muted-foreground">{l}: </span><span className="font-600">{v || "—"}</span></div>;

function VisaFiles({ rec, canEdit, onChanged }) {
  const [docType, setDocType] = useState("other");
  const guard = useStepUp();
  const src = (u) => `${API}${u.replace(/^\/api/, "")}?auth=${localStorage.getItem("token")}`;
  const view = (f) => guard(() => window.open(src(f.url), "_blank", "noreferrer"));
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("File must be under 12 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("doc_type", docType);
    try { await api.post(`/trip-visas/${rec.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }); toast.success("Uploaded — private to this trip"); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };
  const editMeta = async (fid, patch) => { try { await api.put(`/trip-visas/${rec.id}/files/${fid}`, patch); onChanged(); } catch { toast.error("Could not save"); } };
  const files = (rec.files || []).filter((f) => !f.archived);
  return (
    <div data-testid={`visa-files-${rec.id}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Documents &amp; uploads <span className="normal-case tracking-normal">(private by default)</span></div>
      {files.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {files.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-2 border border-zinc-200 px-2 py-1.5 text-xs" data-testid={`visa-file-${f.id}`}>
              <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="font-600 truncate max-w-[160px]">{f.name}</span>
              <span className="text-muted-foreground">{(FILE_TYPES.find(([v]) => v === f.doc_type) || [])[1]}</span>
              {canEdit && <input type="date" defaultValue={f.expiry_date || ""} title="Expiry date" onChange={(e) => editMeta(f.id, { expiry_date: e.target.value })} className="border border-zinc-200 px-1 py-0.5 text-[11px]" />}
              <button onClick={() => view(f)} data-testid={`visa-file-view-${f.id}`} className="text-primary hover:underline ml-auto">View</button>
              <ShareDocButton docType="visa_file" refIds={{ record_id: rec.id, file_id: f.id }} label={f.name} />
              {canEdit && <button onClick={() => editMeta(f.id, { archived: true })} className="text-muted-foreground hover:text-zinc-800" title="Archive">Archive</button>}
              {canEdit && <button onClick={async () => { await api.delete(`/trip-visas/${rec.id}/files/${f.id}`); onChanged(); }} className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className={`${inp} text-xs py-1.5`} data-testid={`visa-file-type-${rec.id}`}>
            {FILE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-primary text-primary cursor-pointer">
            <FileUp className="w-3.5 h-3.5" /> Upload (PDF, JPG, PNG, HEIC)
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" onChange={upload} className="hidden" data-testid={`visa-file-input-${rec.id}`} />
          </label>
        </div>
      )}
    </div>
  );
}

function Comments({ rec, canEdit, onChanged }) {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    await api.post(`/trip-visas/${rec.id}/comments`, { text });
    setText("");
    onChanged();
  };
  return (
    <div data-testid={`visa-comments-${rec.id}`}>
      {(rec.comments || []).length > 0 && (
        <div className="space-y-1 mb-2">
          {rec.comments.map((c) => (
            <div key={c.id} className="text-xs flex items-start gap-2">
              <span className="font-mono text-muted-foreground shrink-0">{c.date}</span>
              <span className="flex-1"><b>{c.author}:</b> {c.text}</span>
              {canEdit && <button onClick={async () => { await api.delete(`/trip-visas/${rec.id}/comments/${c.id}`); onChanged(); }} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add update / comment (e.g. Additional bank statement requested)" data-testid={`visa-comment-input-${rec.id}`}
            className={`${inp} flex-1 text-xs py-1.5`} />
          <button onClick={add} data-testid={`visa-comment-add-${rec.id}`} className="px-3 py-1.5 bg-primary text-white text-xs font-600"><MessageSquare className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function ReminderMenu({ rec, trip }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("visa_expiry");
  const [days, setDays] = useState(30);
  const baseDate = () => {
    if (kind === "visa_expiry") return rec.validity?.valid_until;
    if (kind === "passport_expiry") return rec.passport_expiry;
    if (kind === "arrival_card") return rec.arrival_date || trip.start_date;
    if (kind === "appointment" || kind === "biometrics") return rec.application?.appointment_date;
    return rec.travel_date || trip.start_date;
  };
  const create = async () => {
    const base = baseDate();
    if (!base) { toast.error("Set the relevant date on the record first"); return; }
    const when = new Date(new Date(base + "T09:00:00Z").getTime() - days * 86400000);
    if (when < new Date()) { toast.error("That reminder would be in the past"); return; }
    const label = (REMINDER_KINDS.find(([v]) => v === kind) || [])[1];
    await api.post("/reminders", {
      entity_type: "trip", entity_id: trip.id, trip_id: trip.id,
      title: `${label} — ${rec.passenger_name}`,
      message: `${label} for ${rec.passenger_name} (${rec.destination_iso2 || trip.title}). Base date: ${base}.`,
      remind_at: when.toISOString(), in_app: true, email: true,
    });
    toast.success(`Reminder set for ${when.toISOString().slice(0, 10)} (in-app + email)`);
    setOpen(false);
  };
  return (
    <span className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid={`visa-reminder-btn-${rec.id}`}
        className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs hover:border-primary"><Bell className="w-3.5 h-3.5" /> Set reminder</button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 bg-white border border-zinc-300 shadow-lg p-3 w-72" data-testid={`visa-reminder-menu-${rec.id}`}>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${inp} w-full text-xs mb-2`}>
            {REMINDER_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="flex flex-wrap gap-1 mb-2">
            {[180, 90, 60, 30, 14, 7, 1].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-2 py-1 text-xs border ${days === d ? "bg-primary text-white border-primary" : "border-zinc-300"}`}>{d}d</button>
            ))}
            <input type="number" min="1" value={days} onChange={(e) => setDays(Number(e.target.value) || 1)} className="w-16 border border-zinc-300 px-1.5 py-1 text-xs" />
          </div>
          <div className="text-[11px] text-muted-foreground mb-2">Days before {baseDate() || "the relevant date"} · in-app + email</div>
          <button onClick={create} data-testid={`visa-reminder-save-${rec.id}`} className="w-full px-3 py-1.5 bg-primary text-white text-xs font-600">Create reminder</button>
        </div>
      )}
    </span>
  );
}

export { RecordForm };

function RecordForm({ trip, record, onClose, onSaved }) {
  const isNew = !record.id;
  const [f, setF] = useState({
    passenger_name: record.passenger_name || "", preferred_name: record.preferred_name || "",
    passport_nationality: record.passport_nationality || "", passport_number: "",
    passport_expiry: record.passport_expiry || "", destination_iso2: record.destination_iso2 || (trip.documents?.destination_iso2 || ""),
    travel_date: record.travel_date || trip.start_date || "", arrival_date: record.arrival_date || "",
    departure_date: record.departure_date || "", purpose: record.purpose || "tourism",
    requirement: record.requirement || "", visa_type: record.visa_type || "", visa_type_other: record.visa_type_other || "",
    visa_format: record.visa_format || "", visa_format_other: record.visa_format_other || "",
    status: record.status || "not_started", notes: record.notes || "",
    application: { ...(record.application || {}) }, validity: { ...(record.validity || {}) },
    arrival_card: { ...(record.arrival_card || {}) }, health_docs: [...(record.health_docs || [])],
  });
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setSub = (g, k) => (e) => setF({ ...f, [g]: { ...f[g], [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } });

  const runCheck = async () => {
    if (!f.passport_nationality || !f.destination_iso2) { toast.error("Set passport nationality and destination (ISO codes, e.g. US, IN)"); return; }
    setChecking(true);
    try {
      const { data } = await api.get("/visas/check", { params: { passport: f.passport_nationality.slice(0, 2), destination: f.destination_iso2.slice(0, 2), purpose: f.purpose || "tourism", travel_date: f.travel_date || undefined, passport_expiry: f.passport_expiry || undefined } });
      setCheck(data);
    } catch { toast.error("Could not check requirements right now"); }
    setChecking(false);
  };

  const save = async () => {
    if (!f.passenger_name.trim()) { toast.error("Passenger name is required"); return; }
    try {
      const payload = { ...f, trip_id: trip.id, ...(record.id ? { id: record.id } : {}), ...(check ? { checked_at: new Date().toISOString().slice(0, 10) } : {}) };
      await api.post("/trip-visas", payload);
      toast.success(isNew ? "Passenger visa record added" : "Record updated");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  const addHealth = () => setF({ ...f, health_docs: [...f.health_docs, { id: crypto.randomUUID(), doc_type: "vaccination_certificate" }] });
  const setHealth = (i, k, v) => { const hd = [...f.health_docs]; hd[i] = { ...hd[i], [k]: v }; setF({ ...f, health_docs: hd }); };

  return (
    <div className="border border-violet-300 bg-violet-50/30 p-4 mb-4" data-testid="visa-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <input value={f.passenger_name} onChange={set("passenger_name")} placeholder="Full legal name *" data-testid="visa-f-name" className={inp} />
        <input value={f.preferred_name} onChange={set("preferred_name")} placeholder="Preferred name" className={inp} />
        <input value={f.passport_nationality} onChange={set("passport_nationality")} placeholder="Passport nationality (e.g. US)" data-testid="visa-f-nationality" className={inp} maxLength={2} />
        <input value={f.passport_number} onChange={set("passport_number")} placeholder={record.has_passport_number ? "Passport number (blank = keep)" : "Passport number (optional)"} data-testid="visa-f-passport" className={inp} />
        <label className="text-xs text-muted-foreground">Passport expiry<input type="date" value={f.passport_expiry} onChange={set("passport_expiry")} className={`${inp} w-full`} /></label>
        <input value={f.destination_iso2} onChange={set("destination_iso2")} placeholder="Destination country (e.g. IN)" data-testid="visa-f-destination" className={inp} maxLength={2} />
        <label className="text-xs text-muted-foreground">Travel date<input type="date" value={f.travel_date} onChange={set("travel_date")} className={`${inp} w-full`} /></label>
        <label className="text-xs text-muted-foreground">Expected arrival<input type="date" value={f.arrival_date} onChange={set("arrival_date")} className={`${inp} w-full`} /></label>
        <label className="text-xs text-muted-foreground">Expected departure<input type="date" value={f.departure_date} onChange={set("departure_date")} className={`${inp} w-full`} /></label>
        <select value={f.requirement} onChange={set("requirement")} data-testid="visa-f-requirement" className={inp}>{REQ_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select value={f.visa_type} onChange={set("visa_type")} data-testid="visa-f-type" className={inp}>{TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select value={f.visa_format} onChange={set("visa_format")} className={inp}>{FMT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        {f.visa_type === "other" && <input value={f.visa_type_other} onChange={set("visa_type_other")} placeholder="Visa type — specify" className={inp} />}
        {f.visa_format === "other" && <input value={f.visa_format_other} onChange={set("visa_format_other")} placeholder="Format — specify" className={inp} />}
        <select value={f.status} onChange={set("status")} data-testid="visa-f-status" className={inp}>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>Status: {l}</option>)}</select>
      </div>

      {/* Check entry requirements */}
      <div className="mt-3">
        <button onClick={runCheck} disabled={checking} data-testid="visa-check-btn"
          className="inline-flex items-center gap-1.5 border border-violet-400 text-violet-800 px-3 py-1.5 text-xs font-700 hover:bg-violet-100 disabled:opacity-50">
          <Search className="w-3.5 h-3.5" /> {checking ? "Checking…" : "Check entry requirements"}
        </button>
        {check && <CheckResult check={check} onApply={(req) => setF((prev) => ({ ...prev, requirement: req }))} />}
      </div>

      {/* Application */}
      <Sect title="Application">
        <label className="text-xs text-muted-foreground">Application date<input type="date" value={f.application.date || ""} onChange={setSub("application", "date")} className={`${inp} w-full`} /></label>
        <input value={f.application.reference || ""} onChange={setSub("application", "reference")} placeholder="Application / reference number" data-testid="visa-f-reference" className={inp} />
        <label className="text-xs text-muted-foreground">Appointment date<input type="date" value={f.application.appointment_date || ""} onChange={setSub("application", "appointment_date")} className={`${inp} w-full`} /></label>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!f.application.biometrics_required} onChange={setSub("application", "biometrics_required")} /> Biometrics required</label>
        {f.application.biometrics_required && <label className="text-xs text-muted-foreground">Biometrics completed<input type="date" value={f.application.biometrics_date || ""} onChange={setSub("application", "biometrics_date")} className={`${inp} w-full`} /></label>}
        <label className="text-xs text-muted-foreground">Approval date<input type="date" value={f.application.approval_date || ""} onChange={setSub("application", "approval_date")} className={`${inp} w-full`} /></label>
        <label className="text-xs text-muted-foreground">Visa received<input type="date" value={f.application.received_date || ""} onChange={setSub("application", "received_date")} className={`${inp} w-full`} /></label>
      </Sect>

      {/* Validity */}
      <Sect title="Visa validity">
        <select value={f.validity.period || ""} onChange={setSub("validity", "period")} className={inp}>{PERIOD_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        {f.validity.period === "other" && <input value={f.validity.period_other || ""} onChange={setSub("validity", "period_other")} placeholder="Validity — specify" className={inp} />}
        <label className="text-xs text-muted-foreground">Valid from<input type="date" value={f.validity.valid_from || ""} onChange={setSub("validity", "valid_from")} className={`${inp} w-full`} /></label>
        <label className="text-xs text-muted-foreground">Valid until<input type="date" value={f.validity.valid_until || ""} onChange={setSub("validity", "valid_until")} data-testid="visa-f-valid-until" className={`${inp} w-full`} /></label>
        <select value={f.validity.entries || ""} onChange={setSub("validity", "entries")} className={inp}>{ENTRY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input type="number" min="1" value={f.validity.max_stay_days || ""} onChange={setSub("validity", "max_stay_days")} placeholder="Maximum stay (days)" className={inp} />
        <input value={f.validity.conditions || ""} onChange={setSub("validity", "conditions")} placeholder="Other conditions (e.g. 90 days per visit)" className={`${inp} sm:col-span-2`} />
      </Sect>

      {/* Arrival card */}
      <Sect title="Arrival / entry card">
        <select value={f.arrival_card.required || ""} onChange={setSub("arrival_card", "required")} data-testid="visa-f-ac-required" className={inp}>
          <option value="">Arrival card required?</option><option value="yes">Yes</option><option value="no">No</option><option value="unknown">Unknown / check</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!f.arrival_card.submitted} onChange={setSub("arrival_card", "submitted")} data-testid="visa-f-ac-submitted" /> Submitted</label>
        <label className="text-xs text-muted-foreground">Date submitted<input type="date" value={f.arrival_card.date_submitted || ""} onChange={setSub("arrival_card", "date_submitted")} className={`${inp} w-full`} /></label>
        <input value={f.arrival_card.reference || ""} onChange={setSub("arrival_card", "reference")} placeholder="Reference / confirmation number" className={inp} />
        <label className="text-xs text-muted-foreground">Valid from<input type="date" value={f.arrival_card.valid_from || ""} onChange={setSub("arrival_card", "valid_from")} className={`${inp} w-full`} /></label>
        <label className="text-xs text-muted-foreground">Valid until<input type="date" value={f.arrival_card.valid_until || ""} onChange={setSub("arrival_card", "valid_until")} className={`${inp} w-full`} /></label>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!f.arrival_card.qr_received} onChange={setSub("arrival_card", "qr_received")} /> QR code received</label>
      </Sect>

      {/* Health documents */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Health &amp; travel documents</div>
          <button onClick={addHealth} data-testid="visa-f-health-add" className="text-xs text-primary hover:underline">+ Add document</button>
        </div>
        {f.health_docs.map((h, i) => (
          <div key={h.id} className="grid sm:grid-cols-3 lg:grid-cols-6 gap-1.5 mt-1.5">
            <select value={h.doc_type} onChange={(e) => setHealth(i, "doc_type", e.target.value)} className={`${inp} text-xs`}>{HEALTH_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <input value={h.number || ""} onChange={(e) => setHealth(i, "number", e.target.value)} placeholder="Document number" className={`${inp} text-xs`} />
            <input value={h.issuer || ""} onChange={(e) => setHealth(i, "issuer", e.target.value)} placeholder="Issuing authority" className={`${inp} text-xs`} />
            <input type="date" value={h.issue_date || ""} onChange={(e) => setHealth(i, "issue_date", e.target.value)} title="Issue date" className={`${inp} text-xs`} />
            <input type="date" value={h.expiry_date || ""} onChange={(e) => setHealth(i, "expiry_date", e.target.value)} title="Expiry date" className={`${inp} text-xs`} />
            <button onClick={() => setF({ ...f, health_docs: f.health_docs.filter((_, j) => j !== i) })} className="text-destructive text-xs">Remove</button>
          </div>
        ))}
      </div>

      <textarea value={f.notes} onChange={set("notes")} rows={2} placeholder="Visa / entry notes (e.g. Applied through official immigration website; print two copies before departure)"
        data-testid="visa-f-notes" className={`${inp} w-full mt-3 resize-y`} />
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
        <button onClick={save} data-testid="visa-f-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">{isNew ? "Add passenger record" : "Update record"}</button>
      </div>
    </div>
  );
}

const Sect = ({ title, children }) => (
  <div className="mt-3">
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">{title}</div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end">{children}</div>
  </div>
);

const OUTCOME_TO_REQ = { evisa: "evisa", eta: "eta", visa_on_arrival: "visa_on_arrival",
  visa_required: "required", required: "required", visa_free: "not_required", not_required: "not_required" };

function CheckResult({ check, onApply }) {
  const a = check.assessment || {};
  const links = check.destination?.links || {};
  const req = OUTCOME_TO_REQ[a.outcome] || "unknown";
  return (
    <div className="mt-2 border border-violet-300 bg-white p-3 text-xs space-y-1" data-testid="visa-check-result">
      <div className="font-700 uppercase tracking-wide text-violet-800">{(a.outcome || "check required").replace(/_/g, " ")}</div>
      {a.summary && <div>{a.summary}</div>}
      {a.max_stay && <div><b>Maximum stay:</b> {a.max_stay}</div>}
      {a.passport_validity && <div><b>Passport validity:</b> {a.passport_validity}</div>}
      {a.blank_pages && <div><b>Blank pages:</b> {a.blank_pages}</div>}
      {a.health_notes && <div><b>Health:</b> {a.health_notes}</div>}
      <div className="flex flex-wrap gap-3 pt-1">
        {a.ea_url && <a href={a.ea_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ShieldCheck className="w-3 h-3" /> Official application portal <ExternalLink className="w-3 h-3" /></a>}
        {links.visa_info && <a href={links.visa_info} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Official visa info <ExternalLink className="w-3 h-3" /></a>}
        {links.immigration && <a href={links.immigration} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Immigration / arrival <ExternalLink className="w-3 h-3" /></a>}
      </div>
      <div className="text-amber-700">Requirements last verified {a.last_verified || check.destination?.last_verified || "—"} — recheck official sources before travel; rules change often.</div>
      <button onClick={() => onApply(req)} data-testid="visa-check-apply"
        className="mt-1 px-2.5 py-1 border border-violet-400 text-violet-800 font-700 hover:bg-violet-100">
        Apply "{(REQ_OPTS.find(([v]) => v === req) || [])[1]}" to this record
      </button>
    </div>
  );
}
